/**
 * The scalar family's whole visual vocabulary.
 *
 * Named `*_CLASSES` deliberately, exactly as `selection.tokens.ts` and
 * `structured.tokens.ts` are: the class law (U19) inspects `cva()` literals and
 * `*_CLASSES` initialisers and nothing else, so a differently-named constant
 * puts its string outside the U7/W3 rules meant to govern it.
 *
 * Two deliberate absences, both guarded by `scalar.tokens.spec.ts`:
 *
 *  - **No dimension anywhere.** Not an `h-*`, not a `w-*`, not a `min-h-*`. The
 *    field surface (`ui/field/field.tokens.ts`) is the one place a field height
 *    is written, and a control that adds its own is the defect P10.2's done-when
 *    forbids. The one exception the guard allows is `w-full`, which is a share of
 *    a parent rather than a size.
 *  - **No physical side.** No `left-`/`right-`/`pl-`/`pr-`/`ml-`/`mr-`/`text-left`.
 *    Every class here is logical (`ps-`, `text-start`) or axis-neutral, so Arabic
 *    is not a second layout to maintain (U7).
 */

/* ── shared ─────────────────────────────────────────────────────────────── */

/** The control's own column: the field, then whatever explains it. */
export const SCALAR_SHELL_CLASSES = 'flex w-full flex-col gap-xs';

/** The read-only notice — visible, not merely announced. */
export const SCALAR_READ_ONLY_CLASSES =
  'flex items-center gap-xs text-caption text-muted-foreground';

/**
 * The line under the field that says what the stored value actually is.
 *
 * The whole point of these controls: the operator edits "15 minutes" and reads
 * "= 900 seconds", so the conversion is never something they have to trust.
 */
export const SCALAR_HINT_CLASSES = 'flex flex-wrap items-baseline gap-x-sm gap-y-2xs text-caption';

export const SCALAR_HINT_PRIMARY_CLASSES = 'text-muted-foreground';

/** The same line when what it reports is a problem rather than a fact. */
export const SCALAR_HINT_WARNING_CLASSES = 'text-warning';

/** An adornment that labels the field rather than accepting input — the `%`. */
export const SCALAR_ADORNMENT_CLASSES =
  'flex shrink-0 select-none items-center text-muted-foreground';

/** A row of controls that belong to one field but are not inside its box. */
export const SCALAR_ROW_CLASSES = 'flex flex-wrap items-center gap-sm';

/** The "this value is unset / forever" opt-out beneath a numeric field. */
export const SCALAR_TOGGLE_ROW_CLASSES = 'flex items-center gap-xs';

export const SCALAR_TOGGLE_LABEL_CLASSES = 'text-caption text-muted-foreground';

/** An affordance offered INSTEAD of refusing in silence — "use seconds". */
export const SCALAR_ADOPT_CLASSES =
  'text-caption font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm';

/* ── DurationInput ──────────────────────────────────────────────────────── */

/**
 * The amount slot. `flex-1` rather than a width: the unit chooser beside it
 * sizes to its longest name — which in Arabic is not the same width as in
 * English — and a fixed number width would push one of the two out of the box.
 */
export const DURATION_AMOUNT_CLASSES = 'min-w-0 flex-1';

/** The unit chooser's slot: as wide as its longest name, never wider. */
export const DURATION_UNIT_CLASSES = 'shrink-0 whitespace-nowrap';

/** A unit rendered as text because the field refuses edits. */
export const DURATION_UNIT_STATIC_CLASSES = 'flex shrink-0 items-center text-muted-foreground';

/* ── HeightInput ────────────────────────────────────────────────────────── */

/**
 * A numeric slot inside the height island. `flex-1` rather than a width, and the
 * SAME class for the centimetre box and each half of the imperial pair, so the
 * one box and the two boxes divide the island's width by the same rule.
 */
export const HEIGHT_AMOUNT_CLASSES = 'min-w-0 flex-1';

/**
 * One half of the imperial pair.
 *
 * `text-end` is the load-bearing part, and it is the one thing the first still
 * of this control got wrong: with both halves stretched and their text at the
 * start, `5` sat at one end of its box and its `′` at the other, so the row read
 * `5 ′8 ″` — every mark beside the wrong number. Ending the text binds each
 * value to the mark that follows it, in both directions.
 */
export const HEIGHT_PAIR_CLASSES = 'min-w-0 flex-1 text-end';

/**
 * The INCHES half, which is content-sized rather than stretched.
 *
 * Two boxes both taking half the island push `5′` and `8″` to opposite ends of
 * it, and the pair stops reading as one measurement. Inches are 0–11, so the box
 * is two characters wide — stated as the input's own `size` attribute, which is
 * what HTML has for "this field holds two digits" and is not a pixel this
 * control invented.
 */
export const HEIGHT_INCHES_CLASSES = 'w-auto flex-none text-end';

/**
 * The prime marks after each half of the imperial pair.
 *
 * `′` and `″` rather than a neutral separator: two numbers with a dot between
 * them say nothing about which is feet, and the hint underneath should not be
 * the only place that answers it.
 */
export const HEIGHT_PART_MARK_CLASSES =
  'flex shrink-0 select-none items-center text-muted-foreground';

/* ── PercentInput ───────────────────────────────────────────────────────── */

export const PERCENT_AMOUNT_CLASSES = 'min-w-0 flex-1';

/** The slider under the box: the same value, felt rather than typed. */
export const PERCENT_SLIDER_CLASSES = 'w-full';

/* ── MoneyInput ─────────────────────────────────────────────────────────── */

export const MONEY_AMOUNT_CLASSES = 'min-w-0 flex-1';

/**
 * The ISO code beside the amount.
 *
 * `tabular-nums` is not needed on a code, but `tracking-wide` is: `USD` set
 * beside a number at caption size reads as one word otherwise. The element it
 * dresses carries `dir="ltr"` — a currency code is an identifier, and `SAR`
 * flipped by the bidi algorithm beside a Latin number is a different string.
 */
export const MONEY_CURRENCY_CLASSES =
  'flex shrink-0 select-none items-center text-caption font-medium tracking-wide text-muted-foreground';

/* ── PatternSelect ──────────────────────────────────────────────────────── */

/**
 * The preset chooser's share of its row.
 *
 * A `SelectTrigger` wears `w-full` from the field surface, so without this it
 * takes the whole row and pushes the help button onto a line of its own — which
 * is exactly what the first stills of this control showed.
 */
export const PATTERN_FIELD_CLASSES = 'min-w-0 flex-1';

/** One preset row: the rendered sample first, the raw pattern under it. */
export const PATTERN_OPTION_CLASSES = 'flex flex-col items-start gap-2xs text-start';

export const PATTERN_OPTION_SAMPLE_CLASSES = 'text-body';

/**
 * The raw pattern, always `ltr` and monospaced — it is an identifier, not prose,
 * and `DD/MM/YYYY` reordered by the bidi algorithm is a different pattern.
 */
export const PATTERN_OPTION_CODE_CLASSES = 'font-mono text-caption text-muted-foreground';

export const PATTERN_CUSTOM_CLASSES = 'font-mono';

/** The preview strip: one row per locale, each naming which locale it is. */
export const PATTERN_PREVIEW_CLASSES = 'flex flex-col gap-2xs';

export const PATTERN_PREVIEW_ROW_CLASSES = 'flex items-baseline gap-sm text-caption';

export const PATTERN_PREVIEW_LOCALE_CLASSES = 'shrink-0 text-muted-foreground';

export const PATTERN_PREVIEW_VALUE_CLASSES = 'text-foreground';

/** The token reference inside the help popover. */
export const PATTERN_TOKEN_LIST_CLASSES = 'flex flex-col gap-xs';

export const PATTERN_TOKEN_ROW_CLASSES = 'flex items-baseline justify-between gap-sm text-caption';

export const PATTERN_TOKEN_CODE_CLASSES = 'font-mono text-foreground';

export const PATTERN_TOKEN_SAMPLE_CLASSES = 'text-muted-foreground';
